import axios from 'axios';
import { TopicRequest } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';

export type EntityMetadataType = 'product' | 'subcategory' | 'brand' | 'category' | 'adCampaign' | 'adEntity';

export interface GetEntityMetadataProductRecord {
  id: string;
  title: string;
  subtitle: string;
  translatedTitles: string;
  brandName: string;
  brandId: number;
  categoryName: string;
  categoryId: number;
  subCategoryName: string;
  subCategoryId: number;
  retailerSku: string;
  stacklineSku: string;
  upc: string;
}

export interface GetEntityMetadataSubcategoryRecord {
  id: string;
  subCategoryId: number;
  subCategoryName: string;
}

export interface GetEntityMetadataCategoryRecord {
  id: string;
  categoryId: number;
  categoryName: string;
}

export interface GetEntityMetadataBrandRecord {
  id: string;
  brandId: number;
  brandName: string;
}

export interface GetEntityMetadataCampaignRecord {
  id: string;
  campaignId: string;
  campaignName: string;
  campaignType: string;
  state: string;
}

export interface GetEntityMetadataEntityRecord {
  id: string;
  entityId: string;
  entityName: string;
}

type EntityTypeToResponse = {
  product: GetEntityMetadataProductRecord;
  subcategory: GetEntityMetadataSubcategoryRecord;
  category: GetEntityMetadataCategoryRecord;
  brand: GetEntityMetadataBrandRecord;
  adCampaign: GetEntityMetadataCampaignRecord;
  adEntity: GetEntityMetadataEntityRecord;
};

export interface GetTopicSearchResponseRecord {
  topicId: string;
  topicName: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}

export interface GetTopicSearchResponse {
  records: GetTopicSearchResponseRecord[];
}

export interface TopicTrafficServerProxy {
  getEntityMetadata: <T extends EntityMetadataType>(payload: {
    entityType: T;
    entityIds: string[];
    retailerId: number;
  }) => Promise<EntityTypeToResponse[T][]>;
  getTopicSearchMetrics: (payload: TopicRequest) => Promise<GetTopicSearchResponse>;
  getAllTopicSearchMetrics: (payload: TopicRequest) => Promise<GetTopicSearchResponse>;
}

export const topicTrafficServerProxy: TopicTrafficServerProxy = {
  getEntityMetadata: async (payload) => {
    const response = await axios.post('/api/beacon/GetEntityMetadataInBulk', payload);
    return response.data;
  },
  getTopicSearchMetrics: async (payload) => {
    const response = await axios.post('/api/beacontopics/GetTopicSearchMetrics', payload);
    return response.data;
  },
  getAllTopicSearchMetrics: async (payload) => {
    const response = await axios.post('/api/beacontopics/GetAllTopicSearchMetrics', payload);
    return response.data;
  }
};

// *********************************************
// Type guards
// *********************************************

const isProductRecord = (record: any): record is GetEntityMetadataProductRecord => {
  return 'stacklineSku' in record;
};

const isSubcategoryRecord = (record: any): record is GetEntityMetadataSubcategoryRecord => {
  return 'subCategoryName' in record;
};

const isCategoryRecord = (record: any): record is GetEntityMetadataCategoryRecord => {
  return 'categoryName' in record;
};

const isBrandRecord = (record: any): record is GetEntityMetadataBrandRecord => {
  return 'brandName' in record;
};

const isAdCampaignRecord = (record: any): record is GetEntityMetadataCampaignRecord => {
  return 'campaignName' in record;
};

const isAdEntityRecord = (record: any): record is GetEntityMetadataEntityRecord => {
  return 'entityName' in record;
};

/**
 * Maps the id of each entity to its name
 */
export const getEntityIdToNameMap = <T extends EntityMetadataType>(records: EntityTypeToResponse[T][]) => {
  return records.reduce((acc, record) => {
    if (isProductRecord(record)) {
      acc.set(record.stacklineSku, { name: record.title, record });
    } else if (isSubcategoryRecord(record)) {
      acc.set(`${record.subCategoryId}`, { name: record.subCategoryName, record });
    } else if (isCategoryRecord(record)) {
      acc.set(`${record.categoryId}`, { name: record.categoryName, record });
    } else if (isBrandRecord(record)) {
      acc.set(`${record.brandId}`, { name: record.brandName, record });
    } else if (isAdCampaignRecord(record)) {
      acc.set(`${record.campaignId}`, { name: record.campaignName, record });
    } else if (isAdEntityRecord(record)) {
      acc.set(`${record.entityId}`, { name: record.entityName, record });
    }

    return acc;
  }, new Map<string, { name: string; record: any }>());
};
